/**
 * API Rate Monitor admin page — displays current API usage vs limits,
 * 30-day usage trends, OpenAI token breakdown, and Document Intelligence pages.
 *
 * RBAC: Admin only.
 *
 * Requirements: 55.5, 55.9
 */

import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '@/services/apiClient';

// ─── Types ─────────────────────────────────────────────────────────────

interface APIRateStatus {
  apiName: string;
  currentDayCount: number;
  rateLimit: number;
  usagePercent: number;
  status: 'normal' | 'warning' | 'critical';
  warningThreshold: number;
  criticalThreshold: number;
  remainingQuota: number | null;
  promptTokens: number | null;
  completionTokens: number | null;
  pagesProcessed: number | null;
  featureBreakdown: Record<string, number> | null;
}

interface APIRateTrend {
  date: string;
  callCount: number;
  usagePercent: number;
}

// ─── Component ─────────────────────────────────────────────────────────

export function APIRateMonitor() {
  const [statuses, setStatuses] = useState<APIRateStatus[]>([]);
  const [selectedApi, setSelectedApi] = useState<string | null>(null);
  const [trend, setTrend] = useState<APIRateTrend[]>([]);
  const [loading, setLoading] = useState(true);
  const [trendLoading, setTrendLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadStatuses = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiClient.get<APIRateStatus[]>('mgmt/api-rates');
      setStatuses(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load API rate data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatuses();
  }, [loadStatuses]);

  async function loadTrend(apiName: string) {
    setSelectedApi(apiName);
    setTrendLoading(true);
    try {
      const data = await apiClient.get<APIRateTrend[]>(`mgmt/api-rates/${apiName}`);
      setTrend(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load trend data');
    } finally {
      setTrendLoading(false);
    }
  }

  function getStatusColor(status: string): string {
    switch (status) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'warning': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default: return 'bg-green-100 text-green-800 border-green-200';
    }
  }

  function getBarColor(status: string): string {
    switch (status) {
      case 'critical': return 'bg-red-500';
      case 'warning': return 'bg-yellow-500';
      default: return 'bg-green-500';
    }
  }

  if (loading) return <div ><p className="text-slate-400">Loading API rate data...</p></div>;

  return (
    <div className=" max-w-6xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-lg font-semibold text-text-primary">API Rate Monitor</h1>
        <button
          onClick={loadStatuses}
          className="px-4 py-2 rounded-lg border border-border bg-white text-sm font-medium text-slate-600 hover:bg-page"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-5 bg-red-50 border border-red-200 rounded-xl px-6 py-4 text-red-700 text-sm">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">Dismiss</button>
        </div>
      )}

      {/* API Usage Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {statuses.map(api => (
          <div
            key={api.apiName}
            className="bg-white rounded-lg border border-border p-5 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => loadTrend(api.apiName)}
            role="button"
            tabIndex={0}
            aria-label={`View trend for ${api.apiName}`}
            onKeyDown={e => e.key === 'Enter' && loadTrend(api.apiName)}
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-medium">{api.apiName}</h3>
              <span className={`px-2 py-0.5 rounded text-xs border ${getStatusColor(api.status)}`}>
                {api.status}
              </span>
            </div>

            {/* Usage bar */}
            <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
              <div
                className={`h-2 rounded-full ${getBarColor(api.status)}`}
                style={{ width: `${Math.min(api.usagePercent, 100)}%` }}
              />
            </div>

            <div className="flex justify-between text-sm text-slate-500">
              <span>{api.currentDayCount.toLocaleString()} calls</span>
              <span>{api.usagePercent.toFixed(1)}% of {api.rateLimit.toLocaleString()}</span>
            </div>

            {/* OpenAI-specific: token breakdown */}
            {api.apiName === 'OpenAI' && (api.promptTokens || api.completionTokens) && (
              <div className="mt-2 text-xs text-slate-400 border-t pt-2">
                <div>Prompt tokens: {(api.promptTokens ?? 0).toLocaleString()}</div>
                <div>Completion tokens: {(api.completionTokens ?? 0).toLocaleString()}</div>
                {api.featureBreakdown && Object.keys(api.featureBreakdown).length > 0 && (
                  <div className="mt-1">
                    <span className="font-medium">By feature:</span>
                    {Object.entries(api.featureBreakdown).map(([feature, count]) => (
                      <div key={feature} className="ml-2">{feature}: {count}</div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Document Intelligence-specific: pages processed */}
            {api.apiName === 'DocumentIntelligence' && api.pagesProcessed != null && (
              <div className="mt-2 text-xs text-slate-400 border-t pt-2">
                Pages processed: {api.pagesProcessed.toLocaleString()}
              </div>
            )}

            {api.remainingQuota != null && (
              <div className="text-xs text-slate-400 mt-1">
                Remaining quota: {api.remainingQuota.toLocaleString()}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* 30-Day Trend */}
      {selectedApi && (
        <div className="bg-white rounded-lg border border-border p-5">
          <h2 className="text-lg font-semibold text-text-primary mb-4">
            30-Day Trend: {selectedApi}
          </h2>

          {trendLoading ? (
            <p className="text-slate-400">Loading trend data...</p>
          ) : trend.length === 0 ? (
            <p className="text-slate-400">No historical data available for this API.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-border bg-page">
                    <th className="text-left p-2">Date</th>
                    <th className="text-right p-2">Calls</th>
                    <th className="text-right p-2">Usage %</th>
                    <th className="text-left p-2 w-1/2">Usage</th>
                  </tr>
                </thead>
                <tbody>
                  {trend.map(entry => (
                    <tr key={entry.date} className="border-b border-border hover:bg-page/50">
                      <td className="p-2">{entry.date}</td>
                      <td className="p-2 text-right">{entry.callCount.toLocaleString()}</td>
                      <td className="p-2 text-right">{entry.usagePercent.toFixed(1)}%</td>
                      <td className="p-2">
                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                          <div
                            className="h-1.5 rounded-full bg-blue-500"
                            style={{ width: `${Math.min(entry.usagePercent, 100)}%` }}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
